import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Menu, MenuItem, Stack, Typography, useTheme } from '@mui/material'
import { MENU_PAPER_SX, MENU_ITEM_SX } from '@repo/ui'
import { CheckIcon, ArrowUpRightIcon } from 'lucide-react'

import { InlineSelectButton } from 'components/input'

import { ROUTES } from 'routes'
import {
  useCertificateTriggerSettings,
  useUpdateCertificateActivationTrigger,
  type CertificateActivationTrigger,
} from 'hooks/api'
import {
  ACTIVATION_TRIGGERS,
  DEFAULT_ACTIVATION_TRIGGER,
  activationTriggerTitle,
} from 'constants/activationTriggers'

/**
 * Inline control for the company's certificate activation trigger — the setting
 * that decides WHEN a certificate activates (and is therefore metered/billed).
 * Reads/writes the SAME `certificate_trigger_settings.activation_trigger` the
 * Features page uses (single source of truth). The trigger button matches the
 * "Change plan" button (variant/size/hover parity). Read-only when no companyId.
 */
export const ActivationTriggerSelect = ({
  companyId,
}: {
  companyId?: string | null
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const { settings } = useCertificateTriggerSettings(companyId ?? '', {
    enabled: !!companyId,
  })
  const updateTrigger = useUpdateCertificateActivationTrigger()

  const current: CertificateActivationTrigger =
    settings?.activation_trigger ?? DEFAULT_ACTIVATION_TRIGGER

  // Internal/read-only surface (no companyId): show the current value only.
  if (!companyId) {
    return (
      <Typography variant="body3" color="text.secondary">
        {`Activated: ${activationTriggerTitle(current)}`}
      </Typography>
    )
  }

  const open = Boolean(anchorEl)
  const select = (id: CertificateActivationTrigger) => {
    setAnchorEl(null)
    /*
      WRITE WHEN NOTHING IS STORED, even if the pick equals what is displayed.

      `current` falls back to DEFAULT_ACTIVATION_TRIGGER when the column is null, so a brand
      that opened this control and deliberately chose the default hit `id === current` and
      wrote NOTHING — the column stayed null forever. That is invisible here, but Home's
      "Set your activation rules" step derives from `activation_trigger IS NOT NULL`, so the
      step could never be ticked and block 1's reward was unreachable. Measured: 13 of 13
      companies had a settings row and 0 had a trigger set.

      Choosing the default IS a choice; it just happened to be unrecordable. Re-picking an
      already-stored value stays a no-op, so this adds one write per company, once.
    */
    const isUnset = settings?.activation_trigger == null
    if (id !== current || isUnset) updateTrigger.mutate({ companyId, activationTrigger: id })
  }

  return (
    <>
      {/* Shared trigger markup — see InlineSelectButton on why only the button
          is shared and each menu keeps its own rows. */}
      <InlineSelectButton
        label="Activated:"
        value={activationTriggerTitle(current)}
        ariaLabel="Certificate activation trigger"
        onClick={e => setAnchorEl(e.currentTarget)}
      />

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // House menu chrome (MENU_PAPER_SX): the 8px trigger gap, the 4px paper inset, the matching list
        // inset — MUI's `MuiMenu.list` padding override does not reach a Popover-based
        // menu — and the viewport-safe max height. This dropdown was the one menu in
        // the app that never opted in, so its rows sat flush against the paper edge
        // with no gap between them and read visibly tighter than every other menu.
        slotProps={{
          paper: { sx: { ...MENU_PAPER_SX, width: 400, maxWidth: '92vw' } },
        }}
      >
        {ACTIVATION_TRIGGERS.map(option => {
          const isCurrent = option.id === current
          // From the option, never picked here — these rows draw the same three
          // triggers the Features cards and the picker modal draw, and they have to
          // agree (see constants/activationTriggers).
          const Icon = option.icon
          return (
            <MenuItem
              key={option.id}
              disabled={option.comingSoon}
              onClick={() => select(option.id)}
              // MENU_ITEM_SX carries the house per-row convention: 12px on all four
              // sides (the old `py: 3` set only the vertical, leaving MUI's 16px
              // horizontal default, so rows never aligned with other menus), the
              // nested-radius highlight, and the 4px gap between consecutive rows.
              // These rows are multi-line, hence the flex-start + wrapping overrides.
              sx={{ ...MENU_ITEM_SX, alignItems: 'flex-start', whiteSpace: 'normal' }}
            >
              <Stack
                direction="row"
                alignItems="flex-start"
                spacing={2}
                sx={{ width: '100%' }}
              >
                {/* Leading glyph, mirroring the right-aligned check's 18px slot and
                    its `pt` so both sit on the title line rather than centring on a
                    three-line block. Untinted — the check is what says "current". */}
                <Box
                  aria-hidden
                  sx={{
                    width: 18,
                    flexShrink: 0,
                    pt: 0.5,
                    color: theme.palette.text.secondary,
                  }}
                >
                  <Icon size={18} strokeWidth={1} />
                </Box>
                <Stack spacing={1} sx={{ flexGrow: 1, lineHeight: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="body3" fontWeight={600}>
                      {option.title}
                    </Typography>
                    {option.comingSoon && (
                      <Typography variant="body4" color="text.secondary">
                        (coming soon)
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="body4" color="text.secondary">
                    {option.description}
                  </Typography>
                  {/* Bold the "Best for" lead-in so the use-case line reads as
                      distinct from the description above it. */}
                  <Typography variant="body4" color="text.secondary">
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      Best for
                    </Box>{' '}
                    {option.best_for.replace(/^Best for\s*/i, '')}
                  </Typography>
                </Stack>
                {/* Right-aligned selected check — same treatment as SelectField. */}
                <Box sx={{ width: 18, flexShrink: 0, pt: 0.5 }}>
                  {isCurrent && <CheckIcon size={18} strokeWidth={2} />}
                </Box>
              </Stack>
            </MenuItem>
          )
        })}

        <MenuItem
          onClick={() => {
            setAnchorEl(null)
            navigate(ROUTES.SETTINGS_COMPANY_FEATURES, {
              state: {
                backTo: ROUTES.SETTINGS_COMPANY_BILLING,
                backLabel: 'Billing',
              },
            })
          }}
          // Same 12px horizontal padding as the rows above, so the divider row's text
          // aligns with theirs. Squared off and gapless because a rounded, inset
          // highlight reads wrong under a full-width rule.
          sx={{
            ...MENU_ITEM_SX,
            mb: 0,
            borderRadius: 0,
            borderTop: `1px solid ${theme.palette.primary[100]}`,
            mt: 1,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="body4" fontWeight={500} color="text.primary">
              Learn more in Features
            </Typography>
            <ArrowUpRightIcon size={14} strokeWidth={1.5} />
          </Stack>
        </MenuItem>
      </Menu>
    </>
  )
}
